import React, { useEffect, useState, useContext, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import _isEmpty from "lodash/isEmpty";
import { Modal, message, Spin } from "antd";

import { convertObjectToArray } from "helper/util";
import withFullHeight from "hocs/withFullHeight";
import { selectAllVehicleTypes } from "modules/client/vehicle-types/store/slice";
import { getVehicleTypeList } from "modules/client/vehicle-types/store/thunks";
import ChartContainer from "modules/shared/PickupDropoff/chart/ChartContainer";
import { convertJobsToLocations } from "modules/shared/PickupDropoff/helper";
import MapBox from "modules/shared/PickupDropoff/mapbox/Mapbox";
import PickupDropoffContainer from "modules/shared/PickupDropoff/PickupDropoffContainer";
import { ONE_HOUR_PX } from "modules/shared/PickupDropoff/constants";
import { setGlobalLoading } from "app/global/slice";
import { withPermission } from "hocs/withPermission";
import { UserRole } from "configs/constants";
import { ROUTE_JOB_KIND, useRouteJobResult } from "modules/shared/route-job-result";
import { ProfileContext } from "layouts/private/ProfileContext";
import clientApi from "modules/admin/clients/store/api";

import ProgressModal from "../components/ProgressModal";

import {
  dataJobTypeOptimize,
  deleteShiftStart,
  filterValidRoutes,
  handleRoutesOptimize,
  routesFromEngineInput
} from "../helper";
import { DEFAULT_STOP_ORDER_MODE, STOP_ORDER_MODES } from "../stopOrderModes";
import { dispatchStopOrderMode as dispatchStopOrder } from "../stopOrderDispatch";
import "./ConfirmRoutePage.scss";

import {
  STATE_STATUSES,
  saveAsDraftNewOrder,
  selectDraftId,
  selectNewOrder,
  selectOrderState,
  resetNewOrderStatuses
} from "../store/orderSlice";
import {
  doOptimizeOrder,
  doOptimizeOrderCallback,
  doSortOrderCallback,
  doStoreDraftId
} from "../store/thunks";

const ConfirmRoutePage = ({
  isAdmin,
  redirect = true,
  handleConfirmState = () => null
}) => {
  const profileCtx = useContext(ProfileContext);
  const profile = useMemo(() => profileCtx?.profile, [profileCtx]);
  const [isSelectRouteNumber, setSelectRouteNumber] = useState(false);
  const [numberRoutes, setNumberRoutes] = useState(30);
  const [showOptimizing, setShowOptimizing] = useState(false);
  const [errorMesage, setErrorMessage] = useState("");
  // const [tmpFleet, setTmpFleet] = useState([]);
  const [oneHourPx, setOneHourPx] = useState(ONE_HOUR_PX);

  const [stops, setStops] = useState([]);
  const user = useSelector((state) => state.auth.user);

  const historyRoute = useHistory();

  const newOrder = useSelector(selectNewOrder);
  const orderState = useSelector(selectOrderState);
  const vehicleCategoryList = useSelector(selectAllVehicleTypes);
  const draftIdSelector = useSelector(selectDraftId);
  const clientId = newOrder?.clientId;
  const stopOrderMode = newOrder?.stopOrderMode || DEFAULT_STOP_ORDER_MODE;
  // Track number of stops per route
  const [preferredNoOfStops, setPreferredNoOfStops] = useState(
    profile?.client.preferredNoOfStops
  );

  const dispatch = useDispatch();

  const optimizingResult = useRouteJobResult(
    ROUTE_JOB_KIND.OPTIMIZING,
    draftIdSelector,
    orderState?.routeEngineStatus === STATE_STATUSES.COMPLETED
  );

  useEffect(() => {
    const res = optimizingResult;
    if (!res || !vehicleCategoryList?.length) return;

    if (res?.payload?.data?.error) {
      setErrorMessage(res?.payload?.data?.error);
      setShowOptimizing(false);
      message.error({
        content: res?.payload?.data?.error,
        duration: 5
      });
      dispatch(setGlobalLoading({ isLoading: false }));
      return;
    }

    if (res.event === "routeDraft.optimizing.completed") {
      console.log("[COMPLETED] optimizing routes");

      // Validation - should do more, but for now, checking for bad time windows
      if (res?.payload?.data?.output?.unserved) {
        const unservedJobs = Object.entries(res.payload.data.output.unserved);
        console.log("unservedJobs:", unservedJobs);

        for (const [jobId, errorMessage] of unservedJobs) {
          console.log("Checking job:", jobId, "with error:", errorMessage);
          if (
            errorMessage === "No vehicle available during the specified time windows."
          ) {
            console.log("Condition matched for job:", jobId);
            setErrorMessage(
              "There is an issue with Time Windows. Please ensure you have enough time for pickup and dropoff."
            );
            setShowOptimizing(false);
            message.error({
              content:
                "There is an issue with Time Windows. Please ensure you have enough time for pickup and dropoff.",
              duration: 10
            });
            dispatch(setGlobalLoading({ isLoading: false }));
          }
        }
      }

      const { input } = res.payload.data;
      const routes = routesFromEngineInput(newOrder?.routes, input?.fleet);
      dispatch(
        doOptimizeOrderCallback({
          ...res.payload.data,
          order: {
            ...newOrder,
            routes
          },
          vehicleCategoryList,
          isSelectRouteNumber,
          numberRoutes
        })
      );
      setSelectRouteNumber(false);
    }
    if (res.event === "routeDraft.sorting.completed") {
      console.log("[COMPLETED] sorting routes");
      const { input } = res.payload.data;
      const routes = routesFromEngineInput(newOrder?.routes, input?.fleet);

      dispatch(
        doSortOrderCallback({
          ...res.payload.data,
          order: {
            ...newOrder,
            routes
          },
          vehicleCategoryList,
          isSelectRouteNumber,
          numberRoutes
        })
      );
      setSelectRouteNumber(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optimizingResult]);

  useEffect(() => {
    const jobs = convertObjectToArray(newOrder?.jobs);
    const tmpStops = convertJobsToLocations(jobs);
    setStops(tmpStops);
  }, [newOrder?.jobs]);

  useEffect(() => {
    const arrRoutes = convertObjectToArray(newOrder?.routes);

    if (!isSelectRouteNumber) setNumberRoutes(arrRoutes.length);
    // eslint-disable-next-line
  }, [newOrder?.routes]);

  useEffect(() => {
    dispatch(getVehicleTypeList());
    // eslint-disable-next-line
  }, []);

  // The routing proxy can refuse a request outright (invalid payload, too large), in which
  // case no callback is ever coming. Show why rather than leaving the modal spinning.
  useEffect(() => {
    const routeEngineError = orderState?.routeEngineError;
    if (!routeEngineError) return;
    setErrorMessage(routeEngineError);
    setShowOptimizing(false);
    dispatch(setGlobalLoading({ isLoading: false }));
    message.error({ content: routeEngineError, duration: 10 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderState?.routeEngineError]);

  useEffect(() => {
    const { routeEngineStatus, status } = orderState || {};

    if (errorMesage) return;

    if (
      [routeEngineStatus, status].includes(STATE_STATUSES.PROCESSING) ||
      (routeEngineStatus === STATE_STATUSES.COMPLETED &&
        status !== STATE_STATUSES.COMPLETED)
    ) {
      console.log("[START] optimizing or sorting");
      dispatch(setGlobalLoading({ isLoading: true }));
      setShowOptimizing(true);
    } else {
      console.log("[END] optimizing or sorting");
      setShowOptimizing(false);
    }
    // eslint-disable-next-line
  }, [orderState?.routeEngineStatus, orderState?.status]);

  const dispatchStopOrderMode = ({ draftId, tmpJobs }) =>
    dispatchStopOrder(dispatch, {
      stopOrderMode,
      newOrder,
      tmpJobs,
      draftId,
      userId: user?.sub,
      clientId,
      vehicleCategoryList,
      preferredNoOfStops,
      onError: setErrorMessage
    });

  useEffect(() => {
    if (!newOrder?.jobs) {
      historyRoute.push("/orders/new");
    }

    if (!vehicleCategoryList.length) return;
    if (!newOrder?.isConfirmRoute) {
      const tmpJobs = convertObjectToArray(newOrder?.jobs);
      const visits = dataJobTypeOptimize(tmpJobs);
      const number = Math.ceil((tmpJobs.length || 1) / preferredNoOfStops) || 1;
      let fleet = handleRoutesOptimize(newOrder, number, newOrder?.routes);
      const fleetParam = deleteShiftStart(fleet);
      // setTmpFleet(fleet);
      dispatch(
        saveAsDraftNewOrder({
          data: {
            ...newOrder,
            isConfirmRoute: false
          }
        })
      );
      if (!orderState.status && !orderState.routeEngineStatus) {
        const draftId = new Date().getTime();
        dispatch(doStoreDraftId(draftId));

        if (stopOrderMode === STOP_ORDER_MODES.OPTIMIZE) {
          console.log("Call optimize order at ConfirmRoutePage");
          dispatch(
            doOptimizeOrder({
              userId: user?.sub,
              draftId,
              visits,
              fleet: fleetParam,
              options: {
                polylines: true,
                balance: true,
                distance_type: "distance_travelled"
              },
              order: {
                ...newOrder,
                routes: fleetParam
              },
              vehicleCategoryList,
              tags: [`client_id_${clientId}`]
            })
          );
        } else {
          dispatchStopOrderMode({ draftId, tmpJobs });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleCategoryList, preferredNoOfStops, newOrder?.isConfirmRoute]);

  const redirectToNewJobsPage = () => {
    if (!redirect) return;
    isAdmin ? historyRoute.push("/admin/jobs/new") : historyRoute.push("/orders/new");
  };

  const handleConfirmRoute = () => {
    dispatch(
      saveAsDraftNewOrder({
        data: {
          ...newOrder,
          routes: filterValidRoutes(newOrder.routes),
          isConfirmRoute: true,
          isConfirmLocations: true
        }
      })
    );
    redirectToNewJobsPage();
  };

  const handleCancelBack = useCallback(() => {
    dispatch(resetNewOrderStatuses());
    redirectToNewJobsPage();
    setShowOptimizing(false);
    dispatch(setGlobalLoading({ isLoading: false }));
    handleConfirmState();
    // If we cancel routing return number of stops per route
    // to client default value
    setPreferredNoOfStops(profile?.client.preferredNoOfStops);
  }, []);

  useEffect(() => {
    return () => {
      if (historyRoute.action === "POP") {
        handleCancelBack();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleZoomIn = () => {
    setOneHourPx(oneHourPx + oneHourPx / 10);
  };
  const handleZoomOut = () => {
    setOneHourPx(oneHourPx - oneHourPx / 10);
  };

  if (!newOrder?.jobs) {
    return <div />;
  }

  return (
    <div className="confirm-route-page">
      <PickupDropoffContainer
        stops={stops}
        routes={convertObjectToArray(newOrder?.routes).map((r) => ({
          ...r,
          lat: r.lat || r.startAddressLat,
          lng: r.lng || r.startAddressLng,
          routeLocations: stops?.filter((s) => s.routeId === r.id)
        }))}
        groupName="type"
        onRedirectToNewJobsPage={redirectToNewJobsPage}
        onCancelBack={handleCancelBack}
        oneHourPx={oneHourPx}
      >
        {({
          handleUpdateAllRoutes,
          handleUpdateRoute,
          handleClickMarker,
          handleHoverMarker,
          popupData,
          handleClosePopup,
          goToCoupleStop,
          handleStopDetail,
          markerActive,
          center,
          containerStops,
          originStops,
          containerRoutes,
          mapStops,
          bounds,
          displayAll,
          driverDetail,
          setDriverDetail,
          showDriverDetail,
          setShowDriverDetail,
          groupStops,
          onDragEnd,
          onDragStart,
          handleClickHistory,
          history,
          earliestRoute,
          disabledNextButton,
          disabledPreviousButton,
          dndStatus,
          onRedirectToNewJobsPage,
          onCancelBack
        }) => (
          <>
            <MapBox
              handleClosePopup={handleClosePopup}
              handleClickMarker={handleClickMarker}
              handleHoverMarker={handleHoverMarker}
              stops={mapStops}
              height={500}
              center={center}
              routes={containerRoutes}
              popupData={popupData}
              goToCoupleStop={goToCoupleStop}
              markerActive={markerActive}
              bounds={bounds}
              driverDetail={driverDetail}
              setDriverDetail={setDriverDetail}
              showDriverDetail={showDriverDetail}
              setShowDriverDetail={setShowDriverDetail}
            />
            <ChartContainer
              client={profile?.client}
              maxNumStopsRoute={preferredNoOfStops}
              setMaxNumStopsRoute={setPreferredNoOfStops}
              isSelectRouteNumber={isSelectRouteNumber}
              setSelectRouteNumber={setSelectRouteNumber}
              numberRoutes={numberRoutes}
              setNumberRoutes={setNumberRoutes}
              originStops={originStops}
              handleStopDetail={handleStopDetail}
              handleConfirmRoute={handleConfirmRoute}
              routes={containerRoutes}
              stops={containerStops}
              order={newOrder}
              handleClickMarker={handleClickMarker}
              handleHoverMarker={handleHoverMarker}
              handleClosePopup={handleClosePopup}
              vehicleCategoryList={vehicleCategoryList}
              handleUpdateRoute={handleUpdateRoute}
              handleUpdateAllRoutes={handleUpdateAllRoutes}
              displayAll={displayAll}
              setDriverDetail={setDriverDetail}
              setShowDriverDetail={setShowDriverDetail}
              groupStops={groupStops}
              onDragEnd={onDragEnd}
              onDragStart={onDragStart}
              handleClickHistory={handleClickHistory}
              history={history}
              earliestRoute={earliestRoute}
              disabledNextButton={disabledNextButton}
              disabledPreviousButton={disabledPreviousButton}
              dndStatus={dndStatus}
              onRedirectToNewJobsPage={onRedirectToNewJobsPage}
              onCancelBack={handleCancelBack}
              oneHourPx={oneHourPx}
              onZoomIn={handleZoomIn}
              onZoomOut={handleZoomOut}
              showConfirmButton={errorMesage ? false : true}
            />
          </>
        )}
      </PickupDropoffContainer>
      <Modal
        centered
        zIndex={9900}
        closable={false}
        open={showOptimizing}
        footer={false}
        width={700}
        className="optimizing-modal"
      >
        <div className="optimizing-modal-content">
          <p className="optimize-modal-title">Optimizing Your Route</p>
          <ProgressModal
            numStops={newOrder.dropoffs.length}
            visible={showOptimizing}
            handleCancel={handleCancelBack}
          />
        </div>
      </Modal>
    </div>
  );
};

export default withPermission(withFullHeight(ConfirmRoutePage, 520), [
  UserRole.ADMIN,
  UserRole.MANAGER
]);
